import { HttpErrorResponse } from '@angular/common/http';

export const SESSION_EXPIRED_MESSAGE = 'Your session has expired. Please log in again.';
const OFFLINE_MESSAGE = 'Could not reach the server. Check your connection and try again.';
const SERVER_ERROR_MESSAGE = 'Something went wrong on our side. Please try again.';

/** Keys whose messages are shown as written; any other key is a field name and is shown in front of its message. */
const PLAIN_KEYS = new Set(['error', 'errors', 'detail', 'non_field_errors', 'message']);
/** Machine-readable extras that are never shown. */
const SKIPPED_KEYS = new Set(['code', 'messages', 'status_code']);

/**
 * The readable messages in an API error, worded exactly as the API sent them.
 *
 * The API puts them in `message` (a list of strings). Older responses only carry them under
 * field names, `error` or `detail` -- those are read the same way, so nothing ends up as
 * `[object Object]`.
 */
export function apiErrorMessages(error: unknown): string[] {
  if (!(error instanceof HttpErrorResponse)) {
    const message = (error as { message?: unknown } | null)?.message;
    return typeof message === 'string' && message ? [message] : [];
  }

  const body = error.error;
  // ProgressEvent and ErrorEvent only exist in the browser, not during server-side rendering
  if (error.status === 0 || (typeof ProgressEvent !== 'undefined' && body instanceof ProgressEvent)) {
    return [OFFLINE_MESSAGE];
  }
  if (typeof ErrorEvent !== 'undefined' && body instanceof ErrorEvent) {
    return [body.message || OFFLINE_MESSAGE];
  }
  if (body && typeof body === 'object') {
    if ((body as Record<string, unknown>)['code'] === 'token_not_valid') {
      return [SESSION_EXPIRED_MESSAGE];
    }
    const standard = texts((body as Record<string, unknown>)['message']);
    if (standard.length) {
      return standard;
    }
    const collected = collect(body, '');
    if (collected.length) {
      return collected;
    }
  } else if (typeof body === 'string' && body.trim() && !body.trim().startsWith('<')) {
    return [body.trim()];
  }

  return [error.status >= 500 ? SERVER_ERROR_MESSAGE : `Request failed (${error.status} ${error.statusText})`.trim()];
}

/** All messages of an API error on separate lines, or the fallback when there are none. */
export function apiErrorText(error: unknown, fallback = 'An unexpected error occurred'): string {
  const messages = apiErrorMessages(error);
  return messages.length ? messages.join('\n') : fallback;
}

/** True when the refresh endpoint refused the refresh token itself.
 *  The API answers an expired or unknown refresh token with 400, not 401, so any 4xx counts --
 *  except 408 (timed out) and 429 (rate limited), which are worth trying again. No status
 *  (offline) and 5xx are transient too. */
export function isSessionRejected(error: unknown): boolean {
  return error instanceof HttpErrorResponse && error.status >= 400 && error.status < 500 &&
    error.status !== 408 && error.status !== 429;
}

function texts(value: unknown): string[] {
  if (typeof value === 'string') {
    return value ? [value] : [];
  }
  if (Array.isArray(value)) {
    return value.filter((item): item is string => typeof item === 'string' && !!item);
  }
  return [];
}

function collect(value: unknown, key: string): string[] {
  if (value === null || value === undefined || value === '') {
    return [];
  }
  if (typeof value === 'string' || typeof value === 'number' || typeof value === 'boolean') {
    return [labelled(key, String(value))];
  }
  if (Array.isArray(value)) {
    return value.flatMap((item) => collect(item, key));
  }
  if (typeof value === 'object') {
    const record = value as Record<string, unknown>;
    // e.g. simplejwt's {token_class, token_type, message}
    if (typeof record['message'] === 'string') {
      return [labelled(key, record['message'])];
    }
    return Object.keys(record)
      .filter((name) => !SKIPPED_KEYS.has(name))
      .flatMap((name) => collect(record[name], PLAIN_KEYS.has(name) ? key : name));
  }
  return [];
}

function labelled(key: string, text: string): string {
  return key ? `${fieldLabel(key)}: ${text}` : text;
}

function fieldLabel(key: string): string {
  return key
    .replace(/_/g, ' ')
    .replace(/([A-Z])/g, ' $1')
    .split(' ')
    .filter(Boolean)
    .map((word) => word.charAt(0).toUpperCase() + word.slice(1).toLowerCase())
    .join(' ');
}
